import React, { useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Header } from './components/layout';
import { Toast } from './components/ui';
import { DebugPanel } from './components/DebugPanel';
import { CreatePrediction, PredictionDetail } from './components/predictions';
import { Feed, Channels, History, Analytics, Settings, AuthCallback, AuthLogin, Terms, Privacy, LinkedRolesCallback } from './pages';
import { useAuthStore, useUIStore, useQuotaStore, useServerStore, usePredictionStore } from './store';
import { api } from './lib/api';
import { isDiscordActivity, initDiscordSDK, authenticateWithDiscord, getDiscordSDK } from './lib/discord-sdk';

declare const __APP_VERSION__: string;
declare const __BUILD_HASH__: string;
declare const __BUILD_TIME__: string;

const APP_VERSION = `${__APP_VERSION__}-${__BUILD_HASH__}`;

function MainLayout() {
  const { view } = useUIStore();
  const { selectedPrediction } = usePredictionStore();

  const renderView = () => {
    switch (view) {
      case 'channels':
        return <Channels />;
      case 'create':
        return <CreatePrediction />;
      case 'detail':
        return selectedPrediction ? (
          <PredictionDetail prediction={selectedPrediction} />
        ) : (
          <Feed />
        );
      case 'history':
        return <History />;
      case 'analytics':
        return <Analytics />;
      case 'settings':
        return <Settings />;
      case 'feed':
      default:
        return <Feed />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto p-4 md:p-8">
          {renderView()}
        </div>
      </main>
      <footer className="h-12 flex items-center justify-center gap-4 border-t border-white/10 text-[10px] font-mono text-white/20 tracking-widest uppercase">
        <span>Foretell v{APP_VERSION}</span>
        <DebugPanel />
      </footer>
    </div>
  );
}

function AppBackground() {
  return (
    <>
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 50% 120%, #312e81 0%, #0f172a 70%)',
        }}
      />
      <div
        className="fixed inset-0 z-0 pointer-events-none opacity-40"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />
    </>
  );
}

export default function App() {
  const { setUser, setLoading, isAuthenticated } = useAuthStore();
  const { setQuota, setHasAccess } = useQuotaStore();
  const { setServers, setChannels, setCurrentServer } = useServerStore();
  const [isActivity, setIsActivity] = useState(false);
  const [activityReady, setActivityReady] = useState(false);

  // Initialize Discord SDK if running as Activity
  useEffect(() => {
    const initActivity = async () => {
      const activityCheck = isDiscordActivity();
      console.log('Activity check:', activityCheck, 'URL:', window.location.href);

      if (activityCheck) {
        setIsActivity(true);
        console.log('Running as Discord Activity');

        try {
          const sdk = await initDiscordSDK();
          if (sdk) {
            console.log('Discord SDK initialized');

            // Authenticate with Discord
            const auth = await authenticateWithDiscord();
            if (auth) {
              console.log('Authenticated with Discord:', auth.user.username);

              // Store our JWT token for API calls
              localStorage.setItem('auth_token', auth.apiToken);

              // Set user in store
              setUser({
                id: `discord_${auth.user.id}`,
                discordId: auth.user.id,
                username: auth.user.global_name || auth.user.username,
                discriminator: auth.user.discriminator,
                avatar: auth.user.avatar,
                email: null,
                subscription: 'free',
                quotaUsed: 0,
                quotaResetAt: null,
                createdAt: new Date(),
              });

              // Set up server/channel context from Activity
              const activitySdk = getDiscordSDK();
              if (activitySdk) {
                // Get channel info from SDK
                let channelName = 'Unknown Channel';
                let guildName = 'Unknown Server';
                let guildIcon: string | undefined;
                let channelType = 0;
                let recipientId: string | undefined;
                let recipientName: string | undefined;
                let recipientAvatar: string | undefined;
                let groupIcon: string | undefined;
                let groupOwnerId: string | undefined;

                // If no guildId, this is a DM or Group DM
                const isDM = !activitySdk.guildId;
                if (isDM) {
                  channelType = 1; // Assume DM if no guild
                  channelName = 'Direct Message';
                }

                if (activitySdk.channelId) {
                  try {
                    const channelInfo = await activitySdk.commands.getChannel({ channel_id: activitySdk.channelId });
                    console.log('Channel info from SDK:', JSON.stringify(channelInfo, null, 2));

                    if (channelInfo) {
                      channelType = channelInfo.type;
                      channelName = channelInfo.name || (isDM ? 'Direct Message' : 'Unknown Channel');

                      // Extract guild info if present (for server channels)
                      const channelWithGuild = channelInfo as {
                        guild_id?: string;
                        guild?: { id: string; name: string; icon?: string };
                      };
                      if (channelWithGuild.guild) {
                        guildName = channelWithGuild.guild.name;
                        guildIcon = channelWithGuild.guild.icon || undefined;
                      }

                      // DM - extract recipient info
                      if (channelInfo.type === 1) {
                        channelName = 'Direct Message';
                        const channelWithRecipients = channelInfo as {
                          recipients?: Array<{ id: string; username: string; global_name?: string; avatar?: string }>
                        };
                        console.log('DM recipients:', channelWithRecipients.recipients);
                        const recipient = channelWithRecipients.recipients?.[0];
                        if (recipient) {
                          recipientId = recipient.id;
                          recipientName = recipient.global_name || recipient.username;
                          recipientAvatar = recipient.avatar || undefined;
                          channelName = recipientName; // Show recipient name as channel name
                          console.log('Extracted recipient:', { recipientId, recipientName, recipientAvatar });
                        }
                      }
                      // Group DM - extract group info
                      else if (channelInfo.type === 3) {
                        channelName = channelInfo.name || 'Group Chat';
                        const groupDMInfo = channelInfo as { icon?: string; owner_id?: string };
                        groupIcon = groupDMInfo.icon || undefined;
                        groupOwnerId = groupDMInfo.owner_id || undefined;
                      }
                    }
                  } catch (e) {
                    console.log('Could not get channel info:', e);
                    // Keep the defaults set above based on isDM
                  }
                }

                // Set up the current context - only show the channel we're in
                const serverId = activitySdk.guildId || 'dm';

                setCurrentServer({
                  id: serverId,
                  name: isDM ? 'Direct Messages' : guildName,
                  icon: guildIcon || null,
                  subscription: null,
                  settings: {
                    allowAnonymous: true,
                    defaultDeadlineDays: 7,
                    maxPredictionsPerDay: 10,
                    scheduledPrompts: [],
                  },
                  memberCount: 0,
                  predictionCount: 0,
                });

                // Only show the current channel - no channel list in Activity mode
                if (activitySdk.channelId) {
                  setChannels([{
                    id: activitySdk.channelId,
                    name: channelName,
                    serverId: serverId,
                    serverName: isDM ? 'Direct Messages' : guildName,
                    predictionCount: 0,
                    memberCount: 0,
                  }]);

                  // Register channel visit (fire and forget)
                  const visitData = {
                    channelId: activitySdk.channelId,
                    channelType: channelType,
                    channelName: channelType === 1 ? undefined : channelName, // Don't store name for DMs
                    guildId: activitySdk.guildId || undefined,
                    guildName: activitySdk.guildId ? guildName : undefined,
                    guildIcon: activitySdk.guildId ? guildIcon : undefined,
                    recipientId,
                    recipientName,
                    recipientAvatar,
                    groupIcon,
                    groupOwnerId,
                  };
                  console.log('Registering channel visit:', visitData);
                  api.channelVisits.register(visitData).catch((err) => console.log('Failed to register channel visit:', err));
                }
              }

              // Load additional data
              const entitlementResponse = await api.entitlements.check();
              if (entitlementResponse.success && entitlementResponse.data) {
                setHasAccess(entitlementResponse.data.hasAccess);
              }

              const quotaResponse = await api.quota.get();
              if (quotaResponse.success && quotaResponse.data) {
                setQuota(quotaResponse.data);
              }
            }

            setActivityReady(true);
          }
        } catch (error) {
          console.error('Failed to initialize Discord Activity:', error);
        }

        setLoading(false);
        return;
      }

      // Not an activity, use normal auth flow
      setLoading(false);
    };

    initActivity();
  }, [setUser, setLoading, setQuota, setHasAccess, setCurrentServer, setChannels]);

  // Normal web auth flow (when not running as Activity)
  useEffect(() => {
    if (isActivity) return; // Skip if running as Activity

    const token = localStorage.getItem('auth_token');

    if (!token) {
      setLoading(false);
      return;
    }

    const loadUserData = async () => {
      try {
        const userResponse = await api.auth.getMe();
        if (userResponse.success && userResponse.data) {
          setUser(userResponse.data);

          const entitlementResponse = await api.entitlements.check();
          if (entitlementResponse.success && entitlementResponse.data) {
            setHasAccess(entitlementResponse.data.hasAccess);
          }

          const quotaResponse = await api.quota.get();
          if (quotaResponse.success && quotaResponse.data) {
            setQuota(quotaResponse.data);
          }

          const serversResponse = await api.servers.list();
          if (serversResponse.success && serversResponse.data && serversResponse.data.length > 0) {
            setServers(serversResponse.data);
            setCurrentServer(serversResponse.data[0]);

            // Set channels based on first server (user's actual Discord servers)
            // For now, create a "general" channel for each server - in production,
            // this would come from the API based on where the bot is installed
            setChannels(serversResponse.data.map(server => ({
              id: `${server.id}-general`,
              name: 'predictions',
              serverId: server.id,
              serverName: server.name,
              predictionCount: server.predictionCount || 0,
              memberCount: server.memberCount || 0,
            })));
          } else {
            // No servers - user hasn't added bot to any servers yet
            setServers([]);
            setChannels([]);
          }
        } else {
          localStorage.removeItem('auth_token');
        }
      } catch (error) {
        console.error('Failed to load user data:', error);
        localStorage.removeItem('auth_token');
      }
      setLoading(false);
    };

    loadUserData();
  }, [isActivity, setUser, setLoading, setQuota, setHasAccess, setServers, setCurrentServer]);

  // Redirect unauthenticated web users to channels view
  const { setView } = useUIStore();
  useEffect(() => {
    if (!isDiscordActivity() && !isAuthenticated && !isActivity) {
      setView('channels');
    }
  }, [isAuthenticated, isActivity, setView]);

  return (
    <div className="relative min-h-screen bg-foretell-bg text-slate-100">
      <AppBackground />
      <div className="relative z-10">
        <Routes>
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/login" element={<AuthLogin />} />
          <Route path="/auth/linked-roles/callback" element={<LinkedRolesCallback />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/*" element={<MainLayout />} />
        </Routes>
      </div>
      <Toast />
    </div>
  );
}
